import classNames from "classnames";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Formatter } from "utils/helpers/Format";
import { isDataQuery } from "utils/helpers/Query/splitSQLQuery";

import { useEnableQueryCacheRatio } from "featureFlags/hooks/useEnableQueryCacheRatio";

import Warning from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/DocumentResults/ResultsControls/Warning/Warning";
import { getUserFacingQueryStatementStatus } from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/types";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";

import DurationCell from "components/Datagrid/components/DurationCell";
import StatusCell from "components/Datagrid/components/StatusCell";
import { MAX_DISPLAY_LENGTH } from "components/Datagrid/constant";
import { ExplainIcon, ShowResultsIcon } from "components/Icons";
import CustomTooltip from "components/Tooltip";
import TooltipMessage from "components/Tooltip/TooltipMessage/TooltipMessage";

import { SwitchDocumentActiveQueryStatementCallback } from "../../../../../contexts/DocumentsContext/types";
import {
  DocumentOutputTab,
  ExplainType,
  QueryStatement,
  QueryStatementResult,
} from "../../../../../workspace.types";
import { useProactiveWarnings } from "../../hooks/useProactiveWarnings";
import { useHover } from "../hooks/useHover";

import cellStyles from "../../components/styles/Cell.module.scss";
import styles from "./QueryStatementsTable.module.scss";

type Props = {
  queryStatement: QueryStatement;
  rowNr: number;
  documentId: string;
  switchDocumentActiveQueryStatement: SwitchDocumentActiveQueryStatementCallback;
  onShowPreview: (queryStatement: QueryStatement, ref: HTMLDivElement) => void;
};

type StatisticsCellProps = {
  value: string | number | React.ReactNode;
  cellClassNames?: string;
  extraContent?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const StatisticsCell = (props: StatisticsCellProps) => {
  const { value, cellClassNames, extraContent, onClick } = props;
  return (
    <div
      onClick={onClick}
      className={classNames(cellStyles.cell, cellClassNames, {
        [cellStyles.clickable]: !!onClick,
      })}
    >
      <div className={cellStyles.cell__value}>{value}</div>
      {extraContent}
    </div>
  );
};

type StatementCellProps = {
  hovered: boolean;
  hasResults: boolean;
  hasError: boolean;
  hasExplainAnalyze: boolean;
  content: string;
  openResults: () => void;
  openExplain: () => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  queryStatementId: string;
};

const StatementCell = (props: StatementCellProps) => {
  const {
    content,
    hovered,
    hasResults,
    hasError,
    hasExplainAnalyze,
    openResults,
    openExplain,
    onClick,
    queryStatementId,
  } = props;
  const { t } = useTranslation();
  const { warningComponent } = useProactiveWarnings({
    queryStatementId,
    componentOptions: { greyedOut: true },
  });

  const warningFallback = warningComponent ? (
    <div className={styles.actionsContainer}>{warningComponent}</div>
  ) : null;

  const actions = hovered ? (
    <div className={styles.actionsContainer}>
      {hasExplainAnalyze && (
        <CustomTooltip
          title={t("statistics.show_query_profile")}
          placement="top-start"
        >
          <div
            className={styles.icon}
            onClick={openExplain}
          >
            <ExplainIcon />
          </div>
        </CustomTooltip>
      )}
      {(hasResults || hasError) && (
        <CustomTooltip
          title={t("statistics.show_results")}
          placement="top-start"
        >
          <div
            className={styles.icon}
            onClick={openResults}
            data-testid="hovered-controls-external"
          >
            <ShowResultsIcon />
          </div>
        </CustomTooltip>
      )}
      {warningComponent}
    </div>
  ) : (
    warningFallback
  );
  return (
    <StatisticsCell
      value={content}
      onClick={onClick}
      extraContent={actions}
      cellClassNames={styles.statement}
    />
  );
};

export const getStatisticsRowData = (
  queryStatement: QueryStatement,
  result?: QueryStatementResult
) => {
  const rowCount = queryStatement.resultRows ?? 0;
  const executionTimeSec = queryStatement.statistics?.executionTimeSec ?? 0;
  const scannedBytesCache = queryStatement.statistics?.scannedBytesCache ?? 0;
  const scannedBytesStorage =
    queryStatement.statistics?.scannedBytesStorage ?? 0;

  const bytesRead = queryStatement.statistics?.bytesRead ?? 0;
  const rowsRead = queryStatement.statistics?.rowsRead ?? 0;
  const rowsPerSec = executionTimeSec ? rowsRead / executionTimeSec : 0;
  const queryCacheRatio = scannedBytesCache
    ? (scannedBytesCache / (scannedBytesStorage + scannedBytesCache)) * 100
    : 0;

  const hasResults = !!result?.data;
  const hasError = !!queryStatement.error;
  const hasExplainAnalyze = queryStatement.explain?.[ExplainType.Analyze];

  return {
    rowCount,
    executionTimeSec,
    bytesRead,
    rowsPerSec,
    hasResults,
    hasError,
    hasExplainAnalyze,
    queryCacheRatio,
  };
};

const StatisticsRow = (props: Props) => {
  const { qsResults } = useDocuments();
  const {
    queryStatement,
    rowNr,
    documentId,
    switchDocumentActiveQueryStatement,
    onShowPreview,
  } = props;
  const { t } = useTranslation();
  const { hovered, handleMouseOver, handleMouseLeave } = useHover();
  const enabledQueryCacheRatio = useEnableQueryCacheRatio();

  const {
    rowCount,
    executionTimeSec,
    bytesRead,
    rowsPerSec,
    hasResults,
    hasError,
    hasExplainAnalyze,
    queryCacheRatio,
  } = getStatisticsRowData(queryStatement, qsResults[queryStatement.id]);

  const openResults = useCallback(() => {
    switchDocumentActiveQueryStatement({
      documentId,
      queryStatementIndex: rowNr - 1,
      userSelectedActiveQueryStatementIndexTimestamp: Date.now(),
      activeOutputTab: DocumentOutputTab.Results,
    });
  }, [switchDocumentActiveQueryStatement, documentId, rowNr]);

  const openExplain = useCallback(() => {
    switchDocumentActiveQueryStatement({
      documentId,
      queryStatementIndex: rowNr - 1,
      userSelectedActiveQueryStatementIndexTimestamp: Date.now(),
      activeOutputTab: DocumentOutputTab.Explain,
    });
  }, [switchDocumentActiveQueryStatement, documentId, rowNr]);

  const userFacingStatus = useMemo(() => {
    return getUserFacingQueryStatementStatus(queryStatement);
  }, [queryStatement]);

  const isDataQueryStatement = isDataQuery(queryStatement.content);

  const queryCacheRatioFormatted =
    isDataQueryStatement && queryCacheRatio
      ? `${Formatter.formatNumber(queryCacheRatio, 2, true)}%`
      : t("statistics.n_a");

  const rowCountValue =
    isDataQueryStatement && rowCount >= MAX_DISPLAY_LENGTH ? (
      <div className={styles.rowCount}>
        {rowCount}{" "}
        <Warning
          messages={[
            <TooltipMessage
              key={`warning-${queryStatement.id}`}
              title={t("statistics.results_display_limit")}
            />,
          ]}
        />
      </div>
    ) : (
      rowCount
    );

  return (
    <div
      className={styles.resultRow}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <StatisticsCell
        value={rowNr}
        cellClassNames={styles.statementNr}
      />
      <StatementCell
        content={queryStatement.content}
        queryStatementId={queryStatement.id}
        hovered={hovered}
        hasResults={hasResults}
        hasError={hasError}
        hasExplainAnalyze={hasExplainAnalyze}
        openResults={openResults}
        openExplain={openExplain}
        onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          onShowPreview(queryStatement, e.currentTarget);
          e.stopPropagation();
        }}
      />
      <StatisticsCell
        value={<StatusCell status={userFacingStatus} />}
        cellClassNames={styles.status}
      />
      <StatisticsCell
        value={<DurationCell duration={executionTimeSec} />}
        cellClassNames={styles.duration}
      />
      <StatisticsCell
        value={rowCountValue}
        cellClassNames={styles.rows}
      />
      <StatisticsCell
        value={Formatter.bytesFormatter(bytesRead).formattedString}
        cellClassNames={styles.scanned}
      />
      <StatisticsCell
        value={Formatter.nFormatter(rowsPerSec, 2, true)}
        cellClassNames={styles.rowsPerSec}
      />
      {enabledQueryCacheRatio && (
        <StatisticsCell
          value={queryCacheRatioFormatted}
          cellClassNames={styles.cachedDataRatio}
        />
      )}
    </div>
  );
};

export default StatisticsRow;
